/** Pure JPEG envelope/dimension validation, shared by browser and Functions.
 * This does not resize, decode pixels, contact storage, or alter source images.
 */
export const RISS_JPEG_WIDTH = 640
export const RISS_JPEG_HEIGHT = 480
export const RISS_MAX_JPEG_BYTES = 2 * 1024 * 1024

export function validateRissJpegBase64(base64) {
  const invalid = (code, message, details = {}) => ({ valid: false, errors: [{ code, message }], ...details })
  if (typeof base64 !== 'string' || !base64.length || base64.length % 4 ||
      base64.length > 4 * Math.ceil(RISS_MAX_JPEG_BYTES / 3)) {
    return invalid('INVALID_JPEG_BASE64', 'Use a plain base64 JPEG, without a data-URL prefix, no larger than 2 MiB decoded.')
  }
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(base64)) {
    return invalid('INVALID_JPEG_BASE64', 'JPEG data must be canonical base64 without whitespace, URLs, or prefixes.')
  }
  let binary
  try {
    binary = atob(base64)
    if (btoa(binary) !== base64) return invalid('INVALID_JPEG_BASE64', 'JPEG data must use canonical base64 encoding.')
  } catch { return invalid('INVALID_JPEG_BASE64', 'JPEG data is not valid base64.') }
  const byteLength = binary.length
  if (byteLength > RISS_MAX_JPEG_BYTES) return invalid('JPEG_TOO_LARGE', 'JPEG images may not exceed 2 MiB decoded.', { byteLength })
  const byte = offset => binary.charCodeAt(offset)
  const word = offset => byte(offset) * 256 + byte(offset + 1)
  if (byteLength < 4 || word(0) !== 0xffd8 || word(byteLength - 2) !== 0xffd9) {
    return invalid('INVALID_JPEG_FORMAT', 'Image bytes must be a complete JPEG, not PNG or a renamed file.', { byteLength })
  }
  let offset = 2
  let width, height, sawFrame = false, sawScan = false, scanBytes = 0
  const malformed = () => invalid('INVALID_JPEG_FORMAT', 'JPEG marker structure is incomplete or malformed.', { byteLength })
  while (offset < byteLength) {
    if (byte(offset) !== 0xff) return malformed()
    while (byte(offset) === 0xff) offset++
    if (offset >= byteLength) return malformed()
    const marker = byte(offset++)
    if (marker === 0xd9) {
      if (offset !== byteLength || !sawFrame || !sawScan || !scanBytes) return malformed()
      if (width !== RISS_JPEG_WIDTH || height !== RISS_JPEG_HEIGHT) {
        return invalid('INVALID_JPEG_DIMENSIONS', 'RISS XML photos must be resized to exactly 640 × 480 pixels before export.', { width, height, byteLength })
      }
      return { valid: true, errors: [], width, height, byteLength }
    }
    if (marker === 0 || marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) return malformed()
    if (offset + 2 > byteLength) return malformed()
    const length = word(offset)
    if (length < 2 || offset + length > byteLength - 2) return malformed()
    const isFrame = marker >= 0xc0 && marker <= 0xcf && ![0xc4, 0xc8, 0xcc].includes(marker)
    if (isFrame) {
      if (sawFrame || ![0xc0, 0xc1, 0xc2].includes(marker) || length < 11 || byte(offset + 2) !== 8) return malformed()
      const components = byte(offset + 7)
      if (![1, 3, 4].includes(components) || length !== 8 + 3 * components) return malformed()
      height = word(offset + 3)
      width = word(offset + 5)
      if (!height || !width) return malformed()
      sawFrame = true
    }
    if (marker === 0xda) {
      const components = byte(offset + 2)
      if (!sawFrame || ![1, 2, 3, 4].includes(components) || length !== 6 + 2 * components) return malformed()
      sawScan = true
      offset += length
      while (offset < byteLength) {
        if (byte(offset) !== 0xff) { scanBytes++; offset++; continue }
        if (byte(offset + 1) === 0 || (byte(offset + 1) >= 0xd0 && byte(offset + 1) <= 0xd7)) {
          scanBytes++; offset += 2; continue
        }
        break
      }
    } else offset += length
  }
  return malformed()
}
